import axiosInstance from "utils/request";
import { type ZodType } from "zod";
import {
  type LidarClearanceAxis,
  LidarClearanceAxisSchema,
  type LidarClearanceColorScale,
  LidarClearanceColorScaleSchema,
  type LidarClearanceContextData,
  LidarClearanceContextSchema,
  type LidarClearanceSystem,
  type LidarClearanceSystemAxis,
  LidarClearanceSystemAxisSchema,
  LidarClearanceSystemSchema,
} from "./schemas";

export async function parsed<T>(
  schema: ZodType<T>,
  request: Promise<{ data: unknown }>
): Promise<T> {
  const { data } = await request;
  return schema.parse(data);
}

export function fetchLidarClearanceContext(): Promise<LidarClearanceContextData> {
  return parsed(
    LidarClearanceContextSchema,
    axiosInstance.get("/lidar_clearance")
  );
}

export function createSystem(
  region: string,
  name: string,
  customerId: number | null
): Promise<LidarClearanceSystem> {
  return parsed(
    LidarClearanceSystemSchema,
    axiosInstance.post("/lidar_clearance/systems", {
      region,
      name,
      customer_id: customerId,
    })
  );
}

export function updateSystem(
  region: string,
  systemId: number,
  updates: { name?: string; customer_id?: number | null }
): Promise<LidarClearanceSystem> {
  return parsed(
    LidarClearanceSystemSchema,
    axiosInstance.patch(
      `/lidar_clearance/systems/${region}/${systemId}`,
      updates
    )
  );
}

export function createSystemAxisLink(
  region: string,
  systemId: number,
  axisId: number,
  colorScale: number | null
): Promise<LidarClearanceSystemAxis> {
  return parsed(
    LidarClearanceSystemAxisSchema,
    axiosInstance.post(`/lidar_clearance/systems/${region}/${systemId}/axes`, {
      axis_id: axisId,
      color_scale: colorScale,
    })
  );
}

export function updateSystemAxisLink(
  region: string,
  systemId: number,
  linkId: number,
  colorScale: number | null
): Promise<LidarClearanceSystemAxis> {
  return parsed(
    LidarClearanceSystemAxisSchema,
    axiosInstance.patch(
      `/lidar_clearance/systems/${region}/${systemId}/axes/${linkId}`,
      {
        color_scale: colorScale,
      }
    )
  );
}

export async function deleteSystemAxisLink(
  region: string,
  systemId: number,
  linkId: number
): Promise<void> {
  await axiosInstance.delete(
    `/lidar_clearance/systems/${region}/${systemId}/axes/${linkId}`
  );
}

export type AxisProperties = Omit<LidarClearanceAxis, "region" | "id">;

export function isValidAxisProperties(properties: AxisProperties): boolean {
  return (
    properties.name_en.trim().length > 0 &&
    properties.min_value < properties.max_value
  );
}

export function createAxis(
  region: string,
  fields: AxisProperties
): Promise<LidarClearanceAxis> {
  return parsed(
    LidarClearanceAxisSchema,
    axiosInstance.post("/lidar_clearance/axes", { region, ...fields })
  );
}

export function updateAxis(
  region: string,
  axisId: number,
  updates: Partial<AxisProperties>
): Promise<LidarClearanceAxis> {
  return parsed(
    LidarClearanceAxisSchema,
    axiosInstance.patch(`/lidar_clearance/axes/${region}/${axisId}`, updates)
  );
}

export function createColorScale(
  region: string,
  colors: string[]
): Promise<LidarClearanceColorScale> {
  return parsed(
    LidarClearanceColorScaleSchema,
    axiosInstance.post("/lidar_clearance/color_scales", { region, colors })
  );
}

export function replaceColorScaleStops(
  region: string,
  colorScaleId: number,
  colors: string[]
): Promise<LidarClearanceColorScale> {
  return parsed(
    LidarClearanceColorScaleSchema,
    axiosInstance.put(
      `/lidar_clearance/color_scales/${region}/${colorScaleId}/stops`,
      { colors }
    )
  );
}
